import { ApolloClient } from '@apollo/client';
import axios from 'axios';
import { get } from 'lodash';
import logger from '../common/helpers/logger/logger';
import {
  FetchPropertyArticlesOptions,
  ProjectSearchParams,
} from '../lib/propertyTypes';
import { PROPERTY_ARTICLES_QUERY } from '../queries/propertyPage/propertyArticles';
import config from '../common/config';
import { filterNearbyProjects } from '../common/helpers/utility/filterNearbyProjects';
import {
  USER_AGENT,
  PROJECTNET_ENDPOINTS,
  latestActiveProjectsParams,
} from '../common/helpers/constants';
import { transformProjectnetProjectData } from '../common/helpers/data/transformProjectnetProjectData';
import { ProjectQueryData } from '../queries/searchPage/searchProjects';
import {
  prepareParamsForSearchProjects,
  prepareParamsForSearchProjectsSuggestions,
} from '../common/helpers/utility/prepareParams';

export const makeProjectnetApiRequest = async (
  endpoint: string,
  params: ProjectSearchParams,
) => {
  try {
    const response = await axios.get(
      `${config.projectNetApiBaseUrl}${endpoint}`,
      {
        headers: {
          api_key: config.projectNetApiKey,
          'User-Agent': USER_AGENT,
        },
        params: {
          country: 'thailand',
          language: 'th',
          ...params,
        },
      },
    );
    return response.data;
  } catch (error) {
    logger.logError(
      'projectnetProject.makeProjectnetApiRequest',
      'Error making Projectnet API request',
      {
        endpoint,
        params,
        error: error.message,
      },
    );
    throw error;
  }
};

export const fetchProjectData = async (property_id: string | number) => {
  const params = {
    property_id,
    country: 'thailand',
    language: 'th',
  };
  return await makeProjectnetApiRequest(
    PROJECTNET_ENDPOINTS.PROJECT_DETAIL,
    params,
  );
};

export const fetchNearbyProjects = async (
  districtCode: string,
  currentProjectPropertyId: number,
) => {
  const params = {
    administrative_level_2: districtCode,
    ...latestActiveProjectsParams,
  };

  try {
    const data = await makeProjectnetApiRequest(
      PROJECTNET_ENDPOINTS.SEARCH_PROJECTS,
      params,
    );
    return { data: filterNearbyProjects(data.data, currentProjectPropertyId) };
  } catch (error) {
    logger.logError(
      'fetchNearbyProjects',
      'Error fetching from nearby projects',
      {
        error,
      },
    );
    return { data: [] };
  }
};

export const fetchProjects = async (
  searchQuery: {
    location?: string;
    propertyType?: string;
    freeText?: string;
    minPrice?: string;
    maxPrice?: string;
  },
  searchType?: string,
): Promise<ProjectQueryData['projectListCarouselQuery']> => {
  const params = prepareParamsForSearchProjects(searchQuery, searchType);

  if (
    params.property_types &&
    searchQuery.location &&
    !params.administrative_level_1 &&
    !params.administrative_level_2 &&
    !params.administrative_level_3
  ) {
    return { items: [], totalCount: 0, totalPage: 0 };
  }

  try {
    const projectsData = await makeProjectnetApiRequest(
      PROJECTNET_ENDPOINTS.SEARCH_PROJECTS,
      params,
    );

    const transformedProjects =
      projectsData?.data.map(transformProjectnetProjectData) || [];

    return {
      items: transformedProjects,
      totalCount: projectsData.total,
      totalPage: 1,
    };
  } catch (error) {
    logger.logError(
      'projectnetProject.fetchProjects',
      'Error retrieving projects.',
      {
        error,
      },
    );
    return { items: [], totalCount: 0, totalPage: 0 };
  }
};

export const fetchProjectArticles = async (
  client: ApolloClient<unknown>,
  options: FetchPropertyArticlesOptions,
) => {
  try {
    const variables = {
      projectNetPropertyId: options.projectNetPropertyId,
    };

    const response = await client.query({
      query: PROPERTY_ARTICLES_QUERY,
      variables: variables,
    });

    const data = get(response, 'data', {});
    return data;
  } catch (error) {
    logger.logError(
      'projectnetProject.fetchProjectArticles',
      'Error retrieving project articles from query.',
      {
        options,
        error,
      },
    );
    return {};
  }
};

export const fetchProjectNetSearchSuggestions = async (freeText: string) => {
  const params = prepareParamsForSearchProjectsSuggestions(freeText);

  try {
    return await makeProjectnetApiRequest(
      PROJECTNET_ENDPOINTS.SEARCH_PROJECTS,
      params,
    );
  } catch (error) {
    logger.logError(
      'projectnetProject.fetchProjectNetSearchSuggestions',
      'Error retrieving projects.',
      {
        error,
      },
    );
  }
};
